import { useEffect, useRef, useState } from "react";
import { ChatCircle, X, PaperPlaneRight, Microphone, SpeakerHigh } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { assetUrl } from "../lib/assetUrl";

/* Public Estima onboarding chatbot. Mounts on Landing + Pricing.
   • Fetches /api/chatbot/config — hidden if enabled: false (controlled by admin).
   • Welcomes brand-new visitors after a 1.2-sec delay.
   • Captures exit-intent feedback once per session.
   • Optional Web Speech API voice (toggled via "Speak" button).
*/
const SESSION_KEY = "estima_bot_session";
const EXIT_SHOWN_KEY = "estima_bot_exit_shown";

export default function EstimaBot({ page = "landing" }) {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [exitText, setExitText] = useState("");
  const [exitEmail, setExitEmail] = useState("");
  const [exitDone, setExitDone] = useState(false);
  const [followCursor, setFollowCursor] = useState(false);
  const [bubblePos, setBubblePos] = useState({ x: null, y: null });
  const scrollRef = useRef(null);
  const sessionId = useRef(
    localStorage.getItem(SESSION_KEY) ||
    (() => { const id = Math.random().toString(36).slice(2, 12);
             localStorage.setItem(SESSION_KEY, id); return id; })()
  );

  // Load config + welcome the user
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/chatbot/config");
        if (cancelled) return;
        if (!data?.enabled) return;
        if (data.pages?.length && !data.pages.includes(page)) return;
        setCfg(data);
        // Delayed welcome so the user has a chance to scan the page first
        setTimeout(() => {
          setMessages([{ id: `msg-${Date.now()}-w`, role: "bot", text: data.welcome_message || "Hi!" }]);
          setOpen(true);
        }, 1200);
      } catch { /* silent fail — bot just stays hidden */ }
    })();
    return () => { cancelled = true; };
  }, [page]);

  // Auto-scroll on new message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  // Exit-intent: mouseleave to top, once per session
  useEffect(() => {
    if (!cfg?.follow_cursor_exit) return;
    if (localStorage.getItem(EXIT_SHOWN_KEY) === "1") return;
    const onLeave = (e) => {
      if (e.clientY > 5) return;            // only fire on top-edge leave
      if (localStorage.getItem(EXIT_SHOWN_KEY) === "1") return;
      localStorage.setItem(EXIT_SHOWN_KEY, "1");
      setFollowCursor(true);                // brief 600ms hop after the cursor
      setTimeout(() => { setFollowCursor(false); setExitOpen(true); }, 650);
    };
    document.addEventListener("mouseleave", onLeave);
    return () => document.removeEventListener("mouseleave", onLeave);
  }, [cfg]);

  // Follow-cursor animation
  useEffect(() => {
    if (!followCursor) return;
    const onMove = (e) => setBubblePos({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [followCursor]);

  const speak = (text) => {
    if (!voiceOn || !text) return;
    try {
      if (!("speechSynthesis" in window)) return;
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(text.replace(/[*_`#]/g, ""));
      const voices = window.speechSynthesis.getVoices();
      const v = voices.find((vo) => vo.name === cfg?.voice_name)
        || voices.find((vo) => /female|samantha|google/i.test(vo.name))
        || voices[0];
      if (v) utter.voice = v;
      utter.rate = 1; utter.pitch = 1.05;
      window.speechSynthesis.speak(utter);
    } catch { /* ignore */ }
  };

  const send = async (q) => {
    const text = (q ?? input).trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { id: `msg-${Date.now()}-u`, role: "user", text }]);
    setInput(""); setBusy(true);
    try {
      const { data } = await api.post("/chatbot/ask", {
        question: text, session_id: sessionId.current, page,
      });
      setMessages((m) => [...m, {
        id: `msg-${Date.now()}-b`,
        role: "bot", text: data.answer || "Could you rephrase that?",
        suggestion: data.suggestion,
      }]);
      speak(data.answer);
    } catch {
      setMessages((m) => [...m, {
        id: `msg-${Date.now()}-e`,
        role: "bot",
        text: "I had trouble answering that. Try again in a moment.",
      }]);
    } finally { setBusy(false); }
  };

  const submitExit = async () => {
    if (!exitText.trim()) return;
    try {
      await api.post("/chatbot/exit-feedback", {
        text: exitText, email: exitEmail, session_id: sessionId.current, page,
      });
      setExitDone(true);
    } catch { /* ignore */ }
  };

  if (!cfg) return null;

  return (
    <>
      {/* Bubble */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full shadow-lg border hairline flex items-center justify-center bg-[hsl(var(--primary))] text-white hover:scale-105 transition-transform"
        title={`Chat with ${cfg.name}`}
        data-testid="estima-bot-bubble"
        style={followCursor && bubblePos.x != null ? {
          position: "fixed",
          left: bubblePos.x - 28,
          top: bubblePos.y - 28,
          bottom: "auto", right: "auto",
          transition: "left 90ms ease-out, top 90ms ease-out",
        } : undefined}
      >
        {cfg.avatar_url ? (
          <img src={assetUrl(cfg.avatar_url)} alt={cfg.name}
            className="h-12 w-12 rounded-full object-cover" />
        ) : (
          <ChatCircle size={26} weight="duotone" />
        )}
      </button>

      {/* Chat window */}
      {open && (
        <div
          className="fixed bottom-24 right-5 z-40 w-[360px] max-w-[92vw] bg-background border hairline shadow-xl flex flex-col"
          style={{ height: "min(520px, 80vh)" }}
          data-testid="estima-bot-window"
        >
          <div className="flex items-center justify-between p-3 border-b hairline">
            <div className="flex items-center gap-2 min-w-0">
              {cfg.avatar_url ? (
                <img src={assetUrl(cfg.avatar_url)} alt={cfg.name}
                  className="h-8 w-8 rounded-full object-cover border hairline" />
              ) : (
                <div className="h-8 w-8 rounded-full bg-[hsl(var(--primary))]/15 flex items-center justify-center">
                  <ChatCircle size={16} weight="duotone" className="text-[hsl(var(--primary))]" />
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{cfg.name}</div>
                <div className="text-[10px] text-muted-foreground">Online · ask anything</div>
              </div>
            </div>
            <div className="inline-flex items-center gap-1">
              <button onClick={() => setVoiceOn((v) => !v)}
                className={`p-1.5 rounded ${voiceOn ? "text-[hsl(var(--primary))]" : "text-muted-foreground"}`}
                title={voiceOn ? "Voice ON" : "Voice OFF"}
                data-testid="estima-bot-voice-toggle">
                <SpeakerHigh size={14} weight="duotone" />
              </button>
              <button onClick={() => setOpen(false)} className="p-1.5"
                aria-label="Close" data-testid="estima-bot-close">
                <X size={14} weight="bold" />
              </button>
            </div>
          </div>

          {cfg.body_image_url && (
            <div className="px-3 pt-3">
              <img src={assetUrl(cfg.body_image_url)} alt={cfg.name}
                className="w-full max-h-40 object-contain" />
            </div>
          )}

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="estima-bot-messages">
            {messages.map((m, i) => (
              <div key={m.id || `msg-fallback-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] text-xs px-3 py-2 rounded-lg ${m.role === "user"
                  ? "bg-[hsl(var(--primary))] text-white"
                  : "bg-muted/40 text-foreground"}`}>
                  {m.text}
                  {m.suggestion === "create_account" && (
                    <div className="mt-2 pt-2 border-t hairline">
                      <Link to="/register" className="text-[10px] underline hover:no-underline"
                        data-testid="estima-bot-cta-register">
                        Create your free account → unlocks this feature
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && <div className="text-[10px] text-muted-foreground italic">Thinking…</div>}
          </div>

          {/* Quick replies */}
          {messages.length <= 1 && (
            <div className="flex flex-wrap gap-1 px-3 pb-2">
              {["Rent vs Buy", "What is the AI Advisor?", "Is Estima free?"].map((q) => (
                <button key={q} onClick={() => send(q)}
                  className="text-[10px] px-2 py-1 rounded-full border hairline hover:bg-muted/30"
                  data-testid={`estima-bot-quick-${q.replace(/\W+/g, "-").toLowerCase()}`}>
                  {q}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t hairline p-2 flex items-center gap-1">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything about Estima…" disabled={busy}
              className="flex-1 bg-background px-2 py-1.5 text-xs outline-none"
              data-testid="estima-bot-input" />
            <button type="submit" disabled={busy || !input.trim()}
              className="p-1.5 text-[hsl(var(--primary))] disabled:opacity-30"
              data-testid="estima-bot-send" aria-label="Send">
              <PaperPlaneRight size={16} weight="duotone" />
            </button>
            <SilentIcon />
          </form>
        </div>
      )}

      {/* Exit-intent modal */}
      {exitOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          data-testid="estima-bot-exit-modal">
          <div className="bg-background border hairline max-w-md w-full p-5 space-y-3">
            {!exitDone ? (
              <>
                <div className="eyebrow">Wait!</div>
                <h2 className="font-serif text-2xl">What couldn't you find?</h2>
                <p className="text-xs text-muted-foreground">
                  Tell us in one line — we use this to improve Estima.
                </p>
                <textarea value={exitText} onChange={(e) => setExitText(e.target.value)}
                  rows={3} placeholder="e.g. A property tax calculator for my city…"
                  className="w-full bg-background border hairline px-2 py-1.5 text-xs"
                  data-testid="estima-bot-exit-text" />
                <input value={exitEmail} onChange={(e) => setExitEmail(e.target.value)}
                  placeholder="Email (optional — we'll let you know when it ships)"
                  className="w-full bg-background border hairline px-2 py-1.5 text-xs"
                  data-testid="estima-bot-exit-email" />
                <div className="flex items-center justify-between gap-2 pt-1">
                  <button onClick={() => setExitOpen(false)}
                    className="btn-ghost px-3 py-1.5 text-xs"
                    data-testid="estima-bot-exit-skip">Skip</button>
                  <div className="inline-flex items-center gap-2">
                    {cfg.show_free_account_cta && (
                      <Link to="/register"
                        className="btn-ghost px-3 py-1.5 text-xs"
                        data-testid="estima-bot-exit-register">
                        Create free account
                      </Link>
                    )}
                    <button onClick={submitExit} disabled={!exitText.trim()}
                      className="btn-primary px-3 py-1.5 text-xs disabled:opacity-50"
                      data-testid="estima-bot-exit-submit">Send</button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h2 className="font-serif text-2xl">Thanks — we'll look into it.</h2>
                <p className="text-xs text-muted-foreground">
                  You can always come back. Meanwhile, try a free account to explore
                  what's already live.
                </p>
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button onClick={() => setExitOpen(false)}
                    className="btn-ghost px-3 py-1.5 text-xs"
                    data-testid="estima-bot-exit-done">Close</button>
                  {cfg.show_free_account_cta && (
                    <Link to="/register"
                      className="btn-primary px-3 py-1.5 text-xs"
                      data-testid="estima-bot-exit-done-register">
                      Create free account
                    </Link>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function SilentIcon() {
  // Visual-only mic icon for the future STT add-on — disabled for now.
  return (
    <span className="p-1.5 text-muted-foreground opacity-40" aria-hidden>
      <Microphone size={14} weight="duotone" />
    </span>
  );
}
